<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  value: {
    type: [String, Number, Boolean, Object, Array],
    default: null,
  },
  nodeKey: {
    type: [String, Number],
    default: null,
  },
  depth: {
    type: Number,
    default: 0,
  },
  isLast: {
    type: Boolean,
    default: true,
  },
  parentIsArray: {
    type: Boolean,
    default: false,
  },
  expansionRevision: {
    type: Number,
    default: 0,
  },
  expandAll: {
    type: Boolean,
    default: true,
  },
});

const labels = {
  collapseNode: 'Recolher nó JSON',
  ellipsis: '…',
  expandNode: 'Expandir nó JSON',
  item: 'item',
  items: 'itens',
  key: 'chave',
  keys: 'chaves',
};

const expanded = ref(true);

const isCollection = computed(
  () => props.value !== null && typeof props.value === 'object'
);
const isArray = computed(() => Array.isArray(props.value));
const entries = computed(() => {
  if (!isCollection.value) return [];

  if (isArray.value) {
    return props.value.map((value, index) => [index, value]);
  }

  return Object.keys(props.value).map(key => [key, props.value[key]]);
});
const openToken = computed(() => (isArray.value ? '[' : '{'));
const closeToken = computed(() => (isArray.value ? ']' : '}'));
const collectionLabel = computed(() => {
  const count = entries.value.length;
  const singular = isArray.value ? labels.item : labels.key;
  const plural = isArray.value ? labels.items : labels.keys;

  return `${count} ${count === 1 ? singular : plural}`;
});
const primitiveType = computed(() => {
  if (props.value === null) return 'null';

  return typeof props.value;
});
const primitiveText = computed(() => {
  if (props.value === null) return 'null';
  if (typeof props.value === 'string') return JSON.stringify(props.value);

  return String(props.value);
});
const keyText = computed(() => {
  if (props.nodeKey === null || props.nodeKey === undefined) return '';
  if (props.parentIsArray) return String(props.nodeKey);

  return JSON.stringify(String(props.nodeKey));
});
const toggleLabel = computed(() =>
  expanded.value ? labels.collapseNode : labels.expandNode
);
const lineStyle = computed(() => ({
  '--json-tree-depth': props.depth,
}));

watch(
  () => props.expansionRevision,
  () => {
    expanded.value = props.expandAll;
  }
);

const toggle = () => {
  expanded.value = !expanded.value;
};
</script>

<template>
  <div v-if="isCollection" class="json-tree__node">
    <div class="json-tree__line" :style="lineStyle">
      <button
        type="button"
        class="json-tree__toggle"
        :aria-expanded="expanded"
        :aria-label="toggleLabel"
        @click="toggle"
      >
        <span aria-hidden="true">{{ expanded ? '−' : '+' }}</span>
      </button>
      <span class="json-tree__content">
        <template v-if="keyText">
          <span :class="parentIsArray ? 'json-tree__index' : 'json-tree__key'">
            {{ keyText }}
          </span>
          <span class="json-tree__punctuation">: </span>
        </template>
        <span class="json-tree__punctuation">{{ openToken }}</span>
        <span class="json-tree__meta"> {{ collectionLabel }}</span>
        <span v-if="!expanded" class="json-tree__preview">
          {{ labels.ellipsis }} {{ closeToken }}{{ isLast ? '' : ',' }}
        </span>
      </span>
    </div>

    <div class="json-tree__children" :hidden="!expanded">
      <JsonTreeNode
        v-for="(entry, index) in entries"
        :key="`${String(entry[0])}-${index}`"
        :value="entry[1]"
        :node-key="entry[0]"
        :depth="depth + 1"
        :is-last="index === entries.length - 1"
        :parent-is-array="isArray"
        :expansion-revision="expansionRevision"
        :expand-all="expandAll"
      />
    </div>

    <div
      class="json-tree__line json-tree__close-line"
      :style="lineStyle"
      :hidden="!expanded"
    >
      <span class="json-tree__toggle-spacer" aria-hidden="true" />
      <span class="json-tree__content json-tree__punctuation">
        {{ closeToken }}{{ isLast ? '' : ',' }}
      </span>
    </div>
  </div>

  <div v-else class="json-tree__line" :style="lineStyle">
    <span class="json-tree__toggle-spacer" aria-hidden="true" />
    <span class="json-tree__content">
      <template v-if="keyText">
        <span :class="parentIsArray ? 'json-tree__index' : 'json-tree__key'">
          {{ keyText }}
        </span>
        <span class="json-tree__punctuation">: </span>
      </template>
      <span class="json-tree__primitive">
        <span :class="`json-tree__${primitiveType}`">{{ primitiveText }}</span>
        <span v-if="!isLast" class="json-tree__punctuation">,</span>
      </span>
    </span>
  </div>
</template>

<style scoped>
.json-tree__line {
  align-items: flex-start;
  display: flex;
  min-height: 1.6em;
  padding-left: calc(var(--json-tree-depth) * 1rem);
  padding-right: 0.75rem;
  white-space: pre-wrap;
}

.json-tree__line:hover {
  background: rgb(148 163 184 / 8%);
}

.json-tree__content {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  word-break: normal;
}

.json-tree__toggle,
.json-tree__toggle-spacer {
  flex: 0 0 1.125rem;
  height: 1.125rem;
  margin: 0.1rem 0.25rem 0 0;
  width: 1.125rem;
}

.json-tree__toggle {
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  color: #93c5fd;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-weight: 900;
  justify-content: center;
  line-height: 1;
  padding: 0;
}

.json-tree__toggle:hover,
.json-tree__toggle:focus-visible {
  background: #1e293b;
  border-color: #475569;
  color: #dbeafe;
  outline: none;
}

.json-tree__key {
  color: #7dd3fc;
}

.json-tree__index,
.json-tree__meta,
.json-tree__preview {
  color: #64748b;
}

.json-tree__meta {
  padding-left: 0.5rem;
}

.json-tree__string {
  color: #86efac;
}

.json-tree__number {
  color: #fbbf24;
}

.json-tree__boolean {
  color: #c4b5fd;
}

.json-tree__null {
  color: #94a3b8;
  font-style: italic;
}

.json-tree__punctuation {
  color: #cbd5e1;
}
</style>
